import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import topicGroupEngine from '../YourNotesGroupManager/TopicGroupEngine';
import JInspectorFooter from '../../../../components/shared/JInspectorComponents/JInspectorFooter';
import JInspectorRow from '../../../../components/shared/JInspectorComponents/JInspectorRow';
import useDraggablePanel from '../../../../hooks/useDraggablePanel';
import GroupDslDialog from './GroupDslDialog';
import GroupSettingsTitlebar from './GroupSettingsTitlebar';
import GroupSettingsGrouping from './GroupSettingsGrouping';
import GroupSettingsTopicRules from './GroupSettingsTopicRules';
import GroupSettingsMeta from './GroupSettingsMeta';

import './YourNotesSettings.css';

const DEFAULT_POS = () => ({ top: 8, left: Math.max(8, window.innerWidth / 2 - 260) });

export default function GroupSettingsDlg({ open, group, onClose }) {
    const [form, setForm] = useState({});
    const [dslEditorKey, setDslEditorKey] = useState(null);

    const { pos, dragHandleProps } = useDraggablePanel('GroupSettingsDlg', DEFAULT_POS());

    useEffect(() => {
        if (!open || !group) return;
        setForm({
            rk: group.rk,
            name: group.name,
            goal: group.goal,
            tag: group.tag,
            author: 'user',
            stateId: group.stateId,
            includePatterns: group.includePatterns,
            excludePatterns: group.excludePatterns,
            group0: group.group0,
            group1: group.group1,
            group2: group.group2,
            group3: group.group3,
            group4: group.group4,
            showCompleted: group.showCompleted,
        });
    }, [group, open]);

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
    const isDirty = JSON.stringify(form) !== JSON.stringify(group);

    const handleSave = useCallback(async () => {
        if (!isDirty) return;
        await topicGroupEngine.save(form);
        onClose();
    }, [form, isDirty, onClose]);

    const handleCancel = () => {
        setForm({ ...group });
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e) => {
            // DSL dialog open — laat die zijn eigen Enter/Escape afhandelen, niet de hoofd-dialog.
            if (dslEditorKey) return;
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, isDirty, handleSave, dslEditorKey]);

    if (!open || !group) return null;

    const panel = (
        <div id="group-settings-panel" className="yns-panel" style={{ top: pos.top, left: pos.left }}>
            <GroupSettingsTitlebar
                groupName={group.name}
                isDirty={isDirty}
                onSave={handleSave}
                onCancel={handleCancel}
                dragHandleProps={dragHandleProps}
            />

            <div id="group-settings-body" className="yns-body">
                <div className="yns-section-label">General</div>
                <JInspectorRow label="Name" value={group.name} readonly />

                <GroupSettingsGrouping form={form} onChange={set} onOpenDslEditor={setDslEditorKey} />
                <GroupSettingsTopicRules form={form} onChange={set} />
                <GroupSettingsMeta form={form} group={group} onChange={set} />
            </div>

            <JInspectorFooter isDirty={isDirty} onSave={handleSave} onCancel={handleCancel} />
        </div>
    );

    return (
        <>
            {createPortal(panel, document.body)}
            <GroupDslDialog
                open={!!dslEditorKey}
                gLabel={dslEditorKey ? `G${dslEditorKey.replace('group', '')}` : ''}
                value={dslEditorKey ? form[dslEditorKey] : ''}
                onSave={(newValue) => set(dslEditorKey, newValue)}
                onClose={() => setDslEditorKey(null)}
            />
        </>
    );
}
